import { useTheme } from '@backoffice-frontend/patterns'
import {
  CheckCircleSolidIcon,
  CheckmarkIcon,
  WarningIcon,
} from '@moia-dev/pace-icons'
import { Heading, LoadingIndicator, Text } from '@moia-dev/pace-web'
import { useTranslation } from 'react-i18next'

type TelematicsCheckProps = {
  variant: 'pending' | 'success' | 'overwrite'
  title: string
  subtitle?: boolean
}

export const TelematicsCheck = ({
  variant,
  title,
  subtitle = true,
}: TelematicsCheckProps) => {
  const { t } = useTranslation()
  const theme = useTheme()

  const backgroundColor =
    (variant === 'success' && theme.semantic.ColorSurfaceSuccess) ||
    (variant === 'overwrite' && theme.semantic.ColorWarningAlternative) ||
    (variant === 'pending' && theme.semantic.ColorSurfaceReducedAlternative) ||
    undefined
  const titleColor =
    (variant === 'success' && theme.semantic.ColorSuccessContrast) ||
    (variant === 'overwrite' && theme.semantic.ColorWarningContrast) ||
    (variant === 'pending' && theme.semantic.ColorContentReduced) ||
    undefined
  const captionColor =
    (variant === 'success' && theme.semantic.ColorContentReduced) ||
    (variant === 'overwrite' && theme.semantic.ColorWarningContrast) ||
    (variant === 'pending' && theme.semantic.ColorContentReduced) ||
    undefined

  return (
    <div>
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: theme.spacing(2),
          color: titleColor,
          backgroundColor,
          borderRadius: subtitle ? '8px 8px 0 0' : '8px',
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: titleColor,
        }}
      >
        <Heading size="md" css={{ color: titleColor }}>
          {title}
        </Heading>

        {variant === 'success' && <CheckCircleSolidIcon />}
        {variant === 'overwrite' && <CheckCircleSolidIcon />}
        {variant === 'pending' && <GreyCircle />}
      </div>
      {subtitle && (
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            padding: theme.spacing(1, 2),
            gap: theme.spacing(1),
            color: captionColor,
            borderRadius: '0 0 8px 8px',
            border: `1px solid ${theme.semantic.ColorAccessoryDefault}`,
            borderTop: 'none',
          }}
        >
          {variant === 'pending' && <LoadingIndicator size={16} />}
          {variant === 'overwrite' && (
            <WarningIcon css={{ width: 16, height: 16 }} />
          )}
          {variant === 'success' && (
            <CheckmarkIcon css={{ width: 16, height: 16 }} />
          )}

          <Text size="sm" css={{ color: captionColor }}>
            {variant === 'success' &&
              t('adFleetMap_telematicsCheckSuccessful_label')}
            {variant === 'overwrite' &&
              t('adFleetMap_telematicsCheckSkipped_label')}
            {variant === 'pending' &&
              t('adFleetMap_pendingTelematicsCheck_label')}
          </Text>
        </div>
      )}
    </div>
  )
}

const GreyCircle = () => (
  <div
    css={theme => ({
      width: 20,
      height: 20,
      background: theme.semantic.ColorSurfaceReduced,
      borderRadius: '100%',
    })}
  />
)
